import { memo, useCallback, useMemo } from 'react'
import { Box, Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, ListSubheader, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { DragHandle } from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux'
import { columnOptions } from '../../typings/options.typings'
import { strikeValues } from '../../../util/tradePage'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import Hide from '../../../assets/icons/Hide'
import Show from '../../../assets/icons/Show'
import commonActions from '../../../actions/common.actions'
import { selectOptionChainTableView, selectOptionChainVisibleStrikes, selectShowOptionChainQuantityInLots } from '../../../selectors/common'
import { selectOptionChainColumnState } from '../../../selectors/home'
import { columnsToHide } from '../../../util/optionChainColumns'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { matchPath } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import { OptionChainViewKey } from '../../../assets/constants'
export enum OptionChainTableView {
    NORMAL = 'NORMAL',
    CALLS = 'CALLS',
    PUTS = 'PUTS',
    OTM = 'OTM',
    ITM = 'ITM'
}

const optionChainTableView = [
    { key: OptionChainTableView.NORMAL, label: 'All' },
    { key: OptionChainTableView.CALLS, label: 'Calls' },
    { key: OptionChainTableView.PUTS, label: 'Puts' },
    { key: OptionChainTableView.OTM, label: 'OTM' },
    { key: OptionChainTableView.ITM, label: 'ITM' }
]

const listSubheaderSx = {
    lineHeight: '24px',
    backgroundColor: 'unset',
    fontSize: 12,
    opacity: 0.5,
    mt: 0,
}

type Props = {
    orderOptionChain?: boolean
    optionChainViewKey?: OptionChainViewKey
}

const OptionChainSettingsForm = ({ orderOptionChain = false, optionChainViewKey: optionChainViewKeyProp }: Props) => {
    const dispatch = useDispatch()
    const optionChainViewKey = useMemo(
        () => optionChainViewKeyProp ?? (matchPath(ROUTES.simulator, window.location.pathname) ? OptionChainViewKey.HISTORICAL : OptionChainViewKey.LIVE),
        [optionChainViewKeyProp, window.location.pathname]
    )
    const visibleStrikes: string = useSelector(selectOptionChainVisibleStrikes)
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const chainType = orderOptionChain || optionChainViewKey === OptionChainViewKey.WIDGET ? 'extended' : 'normal'
    const { order: columnOrder, hidden: hiddenColumns, visible: visibleColumns } = useSelector(
        selectOptionChainColumnState(optionChainViewKey, chainType)
    )
    const hiddenColumnSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns])
    const selectedOptionChainTableView = useSelector(selectOptionChainTableView)

    const saveColumns = useCallback((columns: string[], hidden: string[]) => {
        commonActions.saveOptionSelectedColumns(dispatch, {
            columns,
            hidden,
            optionChainViewKey,
            type: chainType,
        })
    }, [dispatch, optionChainViewKey, chainType])

    const handleStrikeChange = useCallback((value: string) => {
        commonActions.setOptionChainVisibleStrikes(dispatch, value)
    }, [dispatch])

    const handleOptionChainTableViewChange = useCallback((view: OptionChainTableView) => {
        commonActions.setOptionChainTableView(dispatch, view)
    }, [dispatch])

    const handleDragEnd = useCallback(
        (sourceIndex: number, destinationIndex: number | undefined) => {
            if (destinationIndex === undefined) return
            let result = [...columnOrder].filter((c) => c !== 'ask')
            const draggedItem = result[sourceIndex]
            if (draggedItem === 'bid' || draggedItem === 'ask') {
                const bidIndex = result.indexOf('bid')
                result.splice(bidIndex, 1)
                result.splice(destinationIndex, 0, 'bid', 'ask')
            } else {
                const [removed] = result.splice(sourceIndex, 1)
                result.splice(destinationIndex, 0, removed)
                const bidIndex = result.indexOf('bid')
                if (bidIndex !== -1) {
                    result.splice(bidIndex, 1, 'bid', 'ask')
                }
            }
            saveColumns(result, hiddenColumns)
        },
        [columnOrder, hiddenColumns, saveColumns]
    )

    const handleToggleColumnVisibility = useCallback((columnKey: string, currentlyHidden: boolean) => {
        const affected = columnsToHide(columnKey)
        if (currentlyHidden) {
            saveColumns(columnOrder, hiddenColumns.filter((c) => !affected.includes(c)))
            return
        }
        const remainingVisible = visibleColumns.filter((c) => !affected.includes(c))
        if (remainingVisible.length < 1) return
        saveColumns(columnOrder, [...new Set([...hiddenColumns, ...affected])])
    }, [columnOrder, hiddenColumns, visibleColumns, saveColumns])

    return (
        <Box component="span" sx={{ display: 'contents' }}>
            {!orderOptionChain && (
                <Box component="span" sx={{ display: 'contents' }}>
                    <ListSubheader sx={listSubheaderSx}>STRIKES</ListSubheader>
                    <ToggleButtonGroup
                        size="small"
                        value={selectedOptionChainTableView}
                        aria-label="text alignment"
                        sx={{ mx: 2, height: '32px' }}
                    >
                        {optionChainTableView.map((s) => (
                            <ToggleButton
                                key={s.key}
                                aria-label="left aligned"
                                selected={s.key == selectedOptionChainTableView}
                                value={s.key}
                                onClick={() => handleOptionChainTableViewChange(s.key)}
                                sx={{ minWidth: '40px' }}
                            >
                                {s.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            )}
            <ListSubheader sx={listSubheaderSx}>NO OF STRIKES</ListSubheader>
            <ToggleButtonGroup
                size="small"
                value={visibleStrikes}
                aria-label="text alignment"
                sx={{ marginLeft: 2, marginRight: 2, height: '32px' }}
            >
                {strikeValues.values.map((s) => (
                    <ToggleButton
                        key={s.value}
                        aria-label="left aligned"
                        selected={s.value == visibleStrikes}
                        value={s.value}
                        onClick={() => handleStrikeChange(s.value)}
                        sx={{ minWidth: '40px' }}
                    >
                        {s.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Divider sx={{ marginTop: 1 }} />
            <ListSubheader sx={listSubheaderSx}>QUANTITY IN LOTS</ListSubheader>
            <ToggleButtonGroup
                size="small"
                value={quantityInLots ? 'Yes' : 'No'}
                aria-label="text alignment"
                sx={{ marginLeft: 2, height: '32px' }}
            >
                <ToggleButton
                    key="Yes"
                    value="Yes"
                    selected={quantityInLots}
                    sx={{ width: '50px' }}
                    onChange={() => {
                        AnalyticsManager.logEvent('Quantity in lots', {
                            source: 'option-chain-bar',
                            value: !quantityInLots
                        })
                        commonActions.setOptionChainQuantityInLots(dispatch, !quantityInLots)
                    }}
                >
                    Yes
                </ToggleButton>
                <ToggleButton
                    key="No"
                    value="No"
                    selected={!quantityInLots}
                    sx={{ width: '50px' }}
                    onChange={() => {
                        AnalyticsManager.logEvent('Quantity in lots', {
                            source: 'option-chain-bar',
                            value: !quantityInLots
                        })
                        commonActions.setOptionChainQuantityInLots(dispatch, !quantityInLots)
                    }}
                >
                    No
                </ToggleButton>
            </ToggleButtonGroup>
            {!orderOptionChain && (
                <Box component="span" sx={{ display: 'contents' }}>
                    <Divider sx={{ marginTop: 1 }} />
                    <ListSubheader sx={listSubheaderSx}>CUSTOMIZE COLUMNS</ListSubheader>
                    <DragDropContext onDragEnd={({ source, destination }) => {
                        handleDragEnd(source.index, destination?.index)
                    }}>
                        <Droppable droppableId="droppable-list">
                            {(provided: any) => (
                                <List dense ref={provided.innerRef} {...provided.droppableProps} sx={{ paddingTop: '0px', paddingBottom: '0px' }}>
                                    {columnOrder.filter((c) => c !== 'ask').map((s, i) => {
                                        let obj = columnOptions.find((c) => c.value === s)
                                        if (s === 'bid') {
                                            obj = columnOptions.find((c) => c.value === 'bidAsk')
                                        }
                                        if (!obj) return null
                                        const isHidden = hiddenColumnSet.has(s) || (s === 'bid' && hiddenColumnSet.has('ask'))
                                        return (
                                            <Draggable draggableId={obj.value} index={i} key={obj.value}>
                                                {(provided: any) => (
                                                    <ListItem
                                                        dense
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        sx={{ opacity: isHidden ? 0.5 : 1 }}
                                                    >
                                                        <ListItemIcon
                                                            data-event="Parameter order changed"
                                                            data-event-source="option-chain-bar"
                                                            sx={{
                                                                minWidth: '20px',
                                                                cursor: 'pointer',
                                                                marginRight: '5px',
                                                            }}
                                                            {...provided.dragHandleProps}
                                                        >
                                                            <DragHandle />
                                                        </ListItemIcon>
                                                        <ListItemText>{obj.label}</ListItemText>
                                                        <IconButton
                                                            sx={{ paddingLeft: 2, paddingY: 0, paddingRight: 0 }}
                                                            onClick={() => {
                                                                AnalyticsManager.logEvent(
                                                                    isHidden ? 'Parameter shown' : 'Parameter hidden',
                                                                    { source: 'option-chain-bar' }
                                                                )
                                                                handleToggleColumnVisibility(s, isHidden)
                                                            }}
                                                        >
                                                            {isHidden ? (
                                                                <Hide sx={{ opacity: 0.5, padding: 0, minWidth: '20px' }} />
                                                            ) : (
                                                                <Show />
                                                            )}
                                                        </IconButton>
                                                    </ListItem>
                                                )}
                                            </Draggable>
                                        )
                                    })}
                                    {provided.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Box>
            )}
        </Box>
    )
}

export default memo(OptionChainSettingsForm)
